import * as React from 'react';
import PropTypes from 'prop-types';
import { useResourceContext, usePreference, useStore, useTranslate } from 'ra-core';

import { Configurable } from 'ra-ui-materialui';
import { Datagrid } from 'react-admin';
import { CustomDatagridEditor } from './CustomDatagridEditor';
import { CustomListNoResults } from './CustomListNoResults';
import { CustomDatagridBody } from './CustomDatagridRow';

const defaultEmpty = <CustomListNoResults />;
const defaultBody = <CustomDatagridBody />;

export const CustomDatagridConfigurable = ({ preferenceKey, omit, empty = defaultEmpty, ...props }) => {
    if (props.optimized) {
        throw new Error('DatagridConfigurable does not support the optimized prop');
    }

    const translate = useTranslate();
    const resource = useResourceContext(props);
    const finalPreferenceKey = preferenceKey || `${resource}.datagrid`;

    const [availableColumns, setAvailableColumns] = useStore(`preferences.${finalPreferenceKey}.availableColumns`, []);
    const [, setOmit] = useStore(`preferences.${finalPreferenceKey}.omit`, omit);
    const [, setColumns] = useStore(`preferences.${finalPreferenceKey}.columns`);

    React.useEffect(() => {
        const baseColumns = React.Children.toArray(props.children)
            .filter(child => React.isValidElement(child))
            .map((child, index) => ({
                index: String(index),
                source: child.props.source,
                label:
                    child.props.columnLabel ??
                    (typeof child.props.label === 'string'
                        ? child.props.label
                        : child.props.label?.props?.title
                          ? child.props.label.props.title
                          : child.props.source
                            ? undefined
                            : translate('ra.configurable.Datagrid.unlabeled', {
                                  column: index,
                                  _: `Unlabeled column #%{column}`,
                              })),
            }));

        const columnSignature = columns =>
            [...columns]
                .map(column => column.source)
                .sort((a, b) => String(a ?? '').localeCompare(String(b ?? '')))
                .join('|');
        const hasColumnSetChanged = !availableColumns.length || columnSignature(baseColumns) !== columnSignature(availableColumns);

        if (hasColumnSetChanged) {
            const visibleIndexes = baseColumns.filter(column => !(omit || []).includes(column.source)).map(column => column.index);
            setAvailableColumns(baseColumns);
            setOmit(omit);
            setColumns(visibleIndexes);
            return;
        }

        const updatedColumns = availableColumns.map(col => {
            const base = baseColumns.find(b => b.index === col.index);
            if (!base) {
                return col;
            }

            if (base.label === col.label && base.source === col.source) {
                return col;
            }

            return { ...col, label: base.label, source: base.source };
        });

        const haveColumnsChanged = updatedColumns.some((col, idx) => col !== availableColumns[idx]);

        if (haveColumnsChanged) {
            setAvailableColumns(updatedColumns);
        }
    }, [availableColumns, props.children, omit, translate, setAvailableColumns, setOmit, setColumns]);

    return (
        <Configurable editor={<CustomDatagridEditor />} preferenceKey={finalPreferenceKey} sx={{ display: 'block', minHeight: 2 }}>
            <CustomDatagridWithPreferences {...props} empty={empty} />
        </Configurable>
    );
};

const CustomDatagridWithPreferences = ({ children, body = defaultBody, ...props }) => {
    const [availableColumns] = usePreference('availableColumns', []);
    const [omit] = usePreference('omit', []);
    const [columns] = usePreference(
        'columns',
        availableColumns.filter(column => !omit?.includes(column.source)).map(column => column.index),
    );
    const childrenArray = React.Children.toArray(children);
    const orderedColumns = Array.isArray(columns) ? columns.map(index => childrenArray[index]).filter(Boolean) : [];

    return (
        <Datagrid {...props} body={body}>
            {orderedColumns.length ? orderedColumns : children}
        </Datagrid>
    );
};

CustomDatagridWithPreferences.propTypes = {
    children: PropTypes.node,
    body: PropTypes.element,
};
